/**
 * Domain types for EditorExposure — the dev-declared catalog of items an app
 * surfaces into editor panels (Add Panel, Section Panel, Add Page).
 *
 * Returned by the per-surface adapters on the host-integration API
 * (e.g. addPanel.getEditorExposureAppComponents).
 *
 * Surface is implicit — set by which adapter produced the AppExposure.
 */

export enum ExposureItemType {
  TEMPLATE = 'TEMPLATE',
  CUSTOM_ELEMENT = 'CUSTOM_ELEMENT',
  EDITOR_REACT_COMPONENT = 'EDITOR_REACT_COMPONENT',
}

type AppExposureBaseItem = {
  /** Stable identifier — component id (CE / ERC) or template id (composition). */
  id: string;
  /** Item title shown in the panel. */
  title: string;
  /** Thumbnail URL. */
  image: string;
};

export type AppExposureTemplateItem = AppExposureBaseItem & {
  type: ExposureItemType.TEMPLATE;
  /**
   * Contexts the editor attaches when adding this template. The template may
   * also depend on other contexts that are already present; those are not
   * included here.
   */
  contextDependencies?: string[];
};

export type AppExposureComponentItem = AppExposureBaseItem & {
  type:
    | ExposureItemType.CUSTOM_ELEMENT
    | ExposureItemType.EDITOR_REACT_COMPONENT;
  /** ERC only — selects a preset on the referenced extension. */
  presetId?: string;
  /** Manifest component-type, resolved from the extension's manifest. */
  componentType?: string;
};

export type AppExposureItem =
  | AppExposureTemplateItem
  | AppExposureComponentItem;

export type AppExposureGroup = {
  /** Developer-chosen label for this section. */
  title: string;
  /** Items the developer placed in this section, in order. */
  items: AppExposureItem[];
};

export type AppExposure = {
  /** The owning app's appDefinitionId. */
  appDefId: string;
  /** The app's display name. */
  title: string;
  /** The app's exposure groups for the surface this AppExposure was produced for. */
  groups: AppExposureGroup[];
};
